import type { DataSourceDeclaration } from '@de/providers/DataSourceManager.types';
import { deriveCacheKey } from './dataSourceCache';
import type { InterpolatedRequest } from './fetchDataSource';
import { readDeclaredKind } from './source-type-registry';
import type { DataSourceHostConfig } from './source-type-registry';
import { resolveDataSourceType } from './source-types';

/**
 * Cache key for a declaration, derived from the request its source type will
 * actually send.
 *
 * This used to re-shape the request per kind alongside each fetcher's own
 * shaping. Two copies of the same shaping is the failure this file is most
 * exposed to: a field one copy includes and the other omits is two different
 * requests sharing an entry, and what you see is one source rendering
 * another's data rather than an error. Asking the descriptor removes the
 * second copy — `buildRequest` is the same function dispatch uses.
 *
 * `coerceRequestBody` is the host rewrite dispatch applies after
 * `buildRequest` and before the network call. The key has to see that rewrite
 * too: a coercer that changes the body, headers, method, or endpoint is a
 * different request, and keying the pre-coercion shape would serve the old
 * response for it.
 */
export function deriveCacheKeyForDecl(
  decl: DataSourceDeclaration,
  context: unknown,
  host: DataSourceHostConfig = {},
  coerceRequestBody?: (request: InterpolatedRequest) => InterpolatedRequest,
): string {
  const sourceType = resolveDataSourceType(decl);
  if (!sourceType) {
    // Namespaced by kind so an unregistered source cannot collide with a
    // registered one, and keyed on the whole declaration because nothing here
    // knows which of its fields shape a request.
    return deriveCacheKey(
      'GET',
      `unknown-kind://${readDeclaredKind(decl)}`,
      undefined,
      decl,
      undefined,
      decl.responsePath,
    );
  }

  const built = sourceType.buildRequest(decl, context, host);
  const request = coerceRequestBody ? coerceRequestBody(built) : built;
  return deriveCacheKey(
    request.method,
    request.endpoint,
    request.query,
    request.body,
    request.headers,
    decl.responsePath,
  );
}
